/**
 * CSV import destination fields — commercial acf_groups_json domains only.
 */
import { COMMERCIAL_DOMAIN_SCHEMAS, COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import { MATERIALS_JSON_FIELD_ID, MATERIALS_JSON_HEADER_ALIASES } from "./materialsJsonImport";
import { PERMITS_JSON_FIELD_ID, PERMITS_JSON_HEADER_ALIASES } from "./permitsJsonImport";

const CSV_SKIP_TYPES = new Set(["repeater", "readonly"]);

/** Schema fields marked computed/readonly that CSV import may still set explicitly. */
const CSV_IMPORTABLE_OVERRIDE_KEYS = new Set(["circuit_id"]);

const DOMAIN_GROUP = {
  [COMMERCIAL_DOMAINS.SUMMARY]: "summary",
  [COMMERCIAL_DOMAINS.ENGINEERING]: "engineering",
  [COMMERCIAL_DOMAINS.CONSTRUCTION]: "construction",
  [COMMERCIAL_DOMAINS.FIBER]: "fiber",
  [COMMERCIAL_DOMAINS.NETWORKING]: "networking",
  [COMMERCIAL_DOMAINS.ACCOUNTING]: "accounting",
};

const DOMAIN_ORDER = [
  COMMERCIAL_DOMAINS.SUMMARY,
  COMMERCIAL_DOMAINS.ENGINEERING,
  COMMERCIAL_DOMAINS.CONSTRUCTION,
  COMMERCIAL_DOMAINS.FIBER,
  COMMERCIAL_DOMAINS.NETWORKING,
  COMMERCIAL_DOMAINS.ACCOUNTING,
];

function importLabelForField(schema, f) {
  const base = `${schema.label} → ${f.label}`;
  if (f.type === "file") return `${base} (URL link)`;
  if (f.type === "multifile") return `${base} (URL links, comma-separated)`;
  return base;
}

function importGroupForField(domainKey, f) {
  const base = DOMAIN_GROUP[domainKey] || "other";
  if (f.type === "file" || f.type === "multifile") return `${base}_files`;
  return base;
}

function buildCommercialImportFields() {
  const fields = [];
  for (const domainKey of DOMAIN_ORDER) {
    const schema = COMMERCIAL_DOMAIN_SCHEMAS[domainKey];
    if (!schema?.fields) continue;
    for (const f of schema.fields) {
      if (f.valueDomain && f.valueDomain !== domainKey) continue;
      const importOverride = CSV_IMPORTABLE_OVERRIDE_KEYS.has(f.key);
      if (f.computed && !importOverride) continue;
      if (f.type === "file" || f.type === "multifile") {
        fields.push({
          id: `${domainKey}.${f.key}`,
          label: importLabelForField(schema, f),
          group: importGroupForField(domainKey, f),
          fieldType: f.type,
        });
        continue;
      }
      if (f.type === "repeater" && f.key === "permits") {
        fields.push({
          id: `${domainKey}.${f.key}`,
          label: `${schema.label} → ${f.label} (JSON)`,
          group: importGroupForField(domainKey, f),
          fieldType: "permits_json",
        });
        fields.push({
          id: MATERIALS_JSON_FIELD_ID,
          label: `${schema.label} → Material totals (JSON)`,
          group: importGroupForField(domainKey, f),
          fieldType: "materials_json",
        });
        continue;
      }
      if (CSV_SKIP_TYPES.has(f.type) && !importOverride) continue;
      fields.push({
        id: `${domainKey}.${f.key}`,
        label: importLabelForField(schema, f),
        group: importGroupForField(domainKey, f),
        fieldType: f.type,
      });
    }
  }
  if (!fields.some((f) => f.id === `${COMMERCIAL_DOMAINS.SUMMARY}.service_speed`)) {
    fields.push({
      id: `${COMMERCIAL_DOMAINS.SUMMARY}.service_speed`,
      label: "Project summary → Service speed",
      group: "summary",
      fieldType: "select",
    });
  }
  return fields;
}

export const CSV_IMPORT_COMMERCIAL_FIELDS = buildCommercialImportFields();

export const CSV_IMPORT_SERVICE_SPEED_FIELDS = new Set(
  CSV_IMPORT_COMMERCIAL_FIELDS.filter((f) => f.id.endsWith(".service_speed")).map((f) => f.id)
);

export const CSV_IMPORT_MATCH_FIELDS = [
  {
    id: "project_summary.work_order",
    label: "Work order",
    hint: "Unique id — required to match existing projects",
  },
];

export const CSV_IMPORT_DESTINATION_FIELDS = [
  { id: "", label: "— Do not import —", group: "meta" },
  ...CSV_IMPORT_MATCH_FIELDS.map((f) => ({ ...f, group: "match" })),
  ...CSV_IMPORT_COMMERCIAL_FIELDS.filter((f) => f.id !== "project_summary.work_order"),
];

/** Common CSV header → commercial field id */
const FIELD_ALIAS_MAP = {
  "project_summary.work_order": ["work_order", "workorder", "wo", "work_order_number"],
  "project_summary.customer": ["customer", "customer_name", "client", "account"],
  "project_summary.new_customer": ["new_customer", "newcustomer"],
  "project_summary.project": ["project", "name", "title", "project_name"],
  "project_summary.type": ["type", "project_type", "work_order_type"],
  "project_summary.subtype": ["subtype", "choices"],
  "project_summary.status": ["status", "project_status"],
  "project_summary.phase": ["phase", "phase_label", "current_phase"],
  "project_summary.subphase": ["subphase", "sub_phase", "current_subphase"],
  "project_summary.foc_date": ["foc_date", "foc"],
  "project_summary.sales_rep": ["sales_rep", "salesrep", "sales_representative"],
  "project_summary.departments": ["departments", "department", "department_involvement"],
  "project_summary.service_order": ["service_order", "service_order_url", "service_order_file"],
  "project_summary.signed_work_order": ["signed_work_order", "signed_work_order_url"],
  "project_summary.executed_service_order": ["executed_service_order", "executed_service_order_url"],
  "project_summary.site_walk": ["site_walk", "sitewalk"],
  "project_summary.service_location": ["service_location", "servicelocation", "address", "location"],
  "project_summary.service_speed": ["summary_service_speed"],
  "project_summary.a_location": ["a_location", "alocation", "a_loc"],
  "project_summary.z_location": ["z_location", "zlocation", "z_loc"],
  "engineering_details.preliminary_route": ["preliminary_route", "preliminary_route_url", "route_file"],
  "engineering_details.construction_footage": [
    "construction_footage",
    "const_footage",
    "footage",
    "engineering_construction_footage",
  ],
  [PERMITS_JSON_FIELD_ID]: PERMITS_JSON_HEADER_ALIASES,
  [MATERIALS_JSON_FIELD_ID]: MATERIALS_JSON_HEADER_ALIASES,
  "engineering_details.site_walk_files": ["site_walk_files", "site_walk_file_urls"],
  "engineering_details.drafter": ["drafter"],
  "construction_details.inspector": ["inspector"],
  "construction_details.construction_start_date": ["construction_start_date", "construction_start", "const_start"],
  "construction_details.construction_end_date": ["construction_end_date", "construction_end", "const_end"],
  "construction_details.construction_days_to_complete": [
    "construction_days_to_complete",
    "days_to_complete",
    "construction_days",
  ],
  "construction_details.construction_include_weekends": [
    "construction_include_weekends",
    "include_weekends",
    "weekend_work",
  ],
  "construction_details.construction_actual_start_date": [
    "construction_actual_start_date",
    "actual_construction_start",
    "actual_start",
  ],
  "construction_details.construction_actual_end_date": [
    "construction_actual_end_date",
    "actual_construction_end",
    "actual_end",
    "construction_completion_date",
  ],
  "construction_details.construction_laborers": ["construction_laborers", "laborers", "construction_laborer"],
  "construction_details.construction_foremen": [
    "construction_foremen",
    "construction_leads",
    "construction_lead",
    "foremen",
    "foreman",
  ],
  "construction_details.asbuilt": ["asbuilt", "asbuilt_urls", "as_built"],
  "construction_details.usa_ticket": ["usa_ticket", "usa_ticket_urls", "usa_tickets"],
  "fiber_details.splicer": ["splicer"],
  "fiber_details.splicing_start_date": ["splicing_start_date", "splicing_start", "splice_start"],
  "fiber_details.splicing_end_date": ["splicing_end_date", "splicing_end", "splice_end"],
  "networking_details.networking_start_date": ["networking_start_date", "networking_start", "network_start"],
  "networking_details.networking_end_date": ["networking_end_date", "networking_end", "network_end", "networking_complete"],
  "networking_details.assigned_technician": ["assigned_technician", "technician", "network_technician"],
  "networking_details.service_speed": ["service_speed", "speed"],
  "networking_details.circuit_id": ["circuit_id", "circuitid", "circuit"],
  "networking_details.connection": ["connection", "connections"],
  "networking_details.handoff": ["handoff"],
  "networking_details.nid_config": ["nid_config", "nid_config_url"],
  "networking_details.topology": ["topology", "topology_url"],
  "networking_details.ips": ["ips", "ip_block"],
  "networking_details.it_contact": ["it_contact"],
  "networking_details.it_number": ["it_number", "it_phone"],
  "networking_details.it_email": ["it_email"],
  "accounting_details.mrc": ["mrc", "monthly_recurring_charge"],
  "accounting_details.nrc": ["nrc", "non_recurring_charge", "non_recurring"],
  "accounting_details.term": ["term", "term_in_months", "term_months"],
  "accounting_details.invoiced": ["invoiced", "invoiced_date", "invoice_date"],
  "accounting_details.accounting_contact": ["accounting_contact"],
  "accounting_details.accounting_number": ["accounting_number", "accounting_phone"],
  "accounting_details.accounting_email": ["accounting_email"],
};

export const CSV_IMPORT_BOOL_FIELDS = new Set(
  CSV_IMPORT_COMMERCIAL_FIELDS.filter((f) => f.fieldType === "bool").map((f) => f.id)
);

export const CSV_IMPORT_NUM_FIELDS = new Set(
  CSV_IMPORT_COMMERCIAL_FIELDS.filter((f) => f.fieldType === "number" || f.fieldType === "currency").map(
    (f) => f.id
  )
);

export const CSV_IMPORT_MULTI_FIELDS = new Set(
  CSV_IMPORT_COMMERCIAL_FIELDS.filter((f) => f.fieldType === "multiselect").map((f) => f.id)
);

export const CSV_IMPORT_FILE_FIELDS = new Set(
  CSV_IMPORT_COMMERCIAL_FIELDS.filter((f) => f.fieldType === "file").map((f) => f.id)
);

export const CSV_IMPORT_MULTIFILE_FIELDS = new Set(
  CSV_IMPORT_COMMERCIAL_FIELDS.filter((f) => f.fieldType === "multifile").map((f) => f.id)
);

export const CSV_IMPORT_PERMITS_JSON_FIELDS = new Set(
  CSV_IMPORT_COMMERCIAL_FIELDS.filter((f) => f.fieldType === "permits_json").map((f) => f.id)
);

export const CSV_IMPORT_MATERIALS_JSON_FIELDS = new Set(
  CSV_IMPORT_COMMERCIAL_FIELDS.filter((f) => f.fieldType === "materials_json").map((f) => f.id)
);

export function isCsvImportFileFieldId(fieldId) {
  return CSV_IMPORT_FILE_FIELDS.has(fieldId) || CSV_IMPORT_MULTIFILE_FIELDS.has(fieldId);
}

function normalizeHeaderKey(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

/**
 * @param {string} csvHeader
 * @returns {string} destination field id or ""
 */
export function suggestDestinationForCsvColumn(csvHeader) {
  const n = normalizeHeaderKey(csvHeader);
  if (!n) return "";
  for (const [fieldId, aliases] of Object.entries(FIELD_ALIAS_MAP)) {
    if (aliases.includes(n) || n === fieldId.replace(".", "_")) return fieldId;
  }
  for (const f of CSV_IMPORT_DESTINATION_FIELDS) {
    if (!f.id) continue;
    const shortKey = f.id.includes(".") ? f.id.split(".").pop() : f.id;
    const sk = normalizeHeaderKey(shortKey);
    if (n === sk) return f.id;
  }
  return "";
}

export function buildAutoColumnMapping(headers) {
  const mapping = {};
  const used = new Set();
  for (const h of headers) {
    let dest = suggestDestinationForCsvColumn(h);
    if (dest && used.has(dest)) dest = "";
    if (dest) used.add(dest);
    mapping[h] = dest;
  }
  return mapping;
}

export function invertMapping(columnToDest) {
  const destToCol = {};
  for (const [col, dest] of Object.entries(columnToDest)) {
    if (!dest || destToCol[dest]) continue;
    destToCol[dest] = col;
  }
  return destToCol;
}

export function invertToColumnMapping(destToCol) {
  const columnToDest = {};
  for (const [dest, col] of Object.entries(destToCol)) {
    if (col) columnToDest[col] = dest;
  }
  return columnToDest;
}

export function groupLabelForImportField(field) {
  const labels = {
    match: "Match key",
    summary: "Project summary",
    summary_files: "Project summary — file links",
    engineering: "Engineering",
    engineering_files: "Engineering — file links",
    construction: "Construction",
    construction_files: "Construction — file links",
    fiber: "Fiber",
    fiber_files: "Fiber — file links",
    networking: "Networking",
    networking_files: "Networking — file links",
    accounting: "Accounting",
    accounting_files: "Accounting — files (URL)",
  };
  return labels[field.group] || field.group;
}

/** Board field groups in column-mapping dropdown order. */
export const CSV_IMPORT_DESTINATION_GROUP_ORDER = [
  "match",
  "summary",
  "summary_files",
  "engineering",
  "engineering_files",
  "construction",
  "construction_files",
  "fiber",
  "fiber_files",
  "networking",
  "networking_files",
  "accounting",
  "accounting_files",
];
